import React, {MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {NavigationProps} from "../constants/Props";
import Screen from "../components/Screen";
import Title from "../components/Title";
import Box, {Direction} from "../components/Box";
import {IDeeplinkStore} from "../stores/DeeplinkStore";
import {observer} from "mobx-react";
import {IGroupStore} from '../stores/GroupStore';
import {toI18N} from "../utils/I18n";
import Alert, {AlertRefProps} from "../components/Alert";
import Button from "../components/Button";
import Icon from "../components/Icon";
import icons from "../css/Icon.module.css";
import classNames from "classnames";
import CopyToClipboard from "react-copy-to-clipboard";
import styles from "../css/Button.module.css";
import modalStyles from "../css/Modal.module.css";
import textStyles from "../css/Text.module.css";
import appStyles from "../css/App.module.css";

import ModalConfirmation from "../components/ModalConfirmation";
import ModalDialogOption from "../components/ModalDialogOption";
import {isMobile} from "react-device-detect";
import {showErrorToast, showInfoToast, usePrevious} from "../utils/HooksHelper";
import ModalDetectHarmfulWords from "../components/ModalDetectHarmfulWords";
import LoadingSpinner from "../components/LoadingSpinner";
import Modal from "../components/Modal";
import {IAuthStore} from "../stores/AuthStore";
import {GlobalContext} from "../constants/Providers";
import avatarStyles from "../css/Avatar.module.css";
import radioButtonStyles from "../css/RadioButtonInput.module.css";
import {AvatarWithText, ItemWithAvatar} from './LandingPageScreen';
import TextInput from "../components/TextInput";
import {useDebouncedCallback} from "use-debounce";
import * as Analytics from "../utils/Analytics";

interface Props extends NavigationProps {
    authenticated: boolean;
    onOpenPreview: (event: MouseEvent) => void
    authStore: IAuthStore;
    imagesAreValid: boolean,
    reUploadAll: Function,
    store: IDeeplinkStore;
    user: ItemWithAvatar,
    textColor: string,
    csbPreviewLink: string;
    landingPagePreviewLink: string;
    groupStore: IGroupStore;
    checkIsHarmful: Function,
    setConfirmModal: (arg: { show: boolean, message?: string, onConfirm?: (e: MouseEvent) => void }) => void,

}

export const LINK_TYPE_COMMUNITY_PAGE = 'community';
export const LINK_TYPE_INVITATION_PAGE= 'invitation';

function LinkDetailsScreen(props: Props) {

    // Props

    const {
        onBack,
        imagesAreValid,
        onOpenPreview,
        reUploadAll,
        csbPreviewLink,
        user,
        textColor,
        setConfirmModal,
        landingPagePreviewLink,
        onNext,
        checkIsHarmful,
        authStore: {
            email,
            setEmail,
            emailCanProceed,
        },
        store: {
            setHasInviter,
            setIsDefault,
            setLinkType,
            linkType,
            hasInviter,
            setDefaultOgDescription,
            setIsPrivate,
            failedGeneratingLink
        },
        groupStore: {
            name: groupName,
            selectId,
            isSelectIdAvailable,
            isCheckingSelectId,
            checkingForSelectId,
            canProceedForCreatingGroup,
            canProceedForSelectingGroup,
            setSelectIdIfPossible
        },
        isActive,
    } = props;

    const prevIsActive = usePrevious(isActive);
    useEffect(() => {
        if (!prevIsActive && isActive) {
            Analytics.logEvent(Analytics.events.land_on_link_settings_page);
        }
    });


    const [showModal, setShowModal] = useState(false);
    const [isCheckingWords, setIsCheckingWords] = useState(false);
    const [showModalDetectHarmfulWords, setShowModalDetectHarmfulWords] = useState(false);
    const [showPreviewOptions, setShowPreviewOptions] = useState(false);
    const [harmfulWords, setHarmfulWords] = useState([]);

    const {editDeeplinkIsExpired} = useContext(GlobalContext);

    const [validateGroup] = useDebouncedCallback((value: string) => {
            setSelectIdIfPossible(value);
        },
        500,
        {
            leading: false
        }
    );


    // Refs

    const alertRef = useRef<AlertRefProps>(null);

    // Callbacks

    const onProceed = useCallback(() => {
        const handle = async () => {
            setIsCheckingWords(true)
            const {isHarmful, words} = await checkIsHarmful();
            setIsCheckingWords(false)
            setHarmfulWords(words)
            const setModal = isHarmful ? setShowModalDetectHarmfulWords : setShowModal
            setModal(true)
        }
        if (!imagesAreValid) {
            info(toI18N('info_image_still_upload'));
            reUploadAll();
            return
        }
        if (editDeeplinkIsExpired) {
            setConfirmModal({
                show: true,
                message: toI18N("about_to_revived_deeplink"),
                onConfirm: async () => {
                    setConfirmModal({show: false});
                    handle();
                }
            });
        } else {
            handle();
        }

    }, [editDeeplinkIsExpired, checkIsHarmful, setConfirmModal])

    const onContinueWithHarmfulWords = useCallback(() => {
        setShowModalDetectHarmfulWords(false)
        setShowModal(true)
    }, [])

    const onFinishCustomizing = useCallback(
        (e: MouseEvent) => {
            setShowModal(false);
            onNext && onNext(e)
        }, [onNext, setShowModal]
    );

    const info = useCallback(
        (text) => {
            showInfoToast(alertRef, text);
        },
        [alertRef],
    );

    const handleOnNext = useCallback(
        (e) => {
            if (!imagesAreValid) {
                info(toI18N('info_image_still_upload'));
                reUploadAll();
                return
            }
            onNext && onNext(e)
        },
        [imagesAreValid, onNext, reUploadAll, info],
    );

    const onValueChange = (event: any) => {
        switch (event.target.value) {
            case LINK_TYPE_COMMUNITY_PAGE:
                setIsPrivate(false);
                setIsDefault(true);
                break;
            case LINK_TYPE_INVITATION_PAGE:
                setIsPrivate(true);
                setIsDefault(false);
                break;
        }
        setLinkType(event.target.value);
    };

    // Effects

    useEffect(() => {

        if (failedGeneratingLink) {
            showErrorToast(alertRef, toI18N('link_details_cant_generate_link'));
        }

    }, [failedGeneratingLink]);

    useEffect(() => {
        setDefaultOgDescription(groupName)
        // eslint-disable-next-line
    }, []); // mimic component did mount

    const OPTIONS = useMemo(() => {
        return [
            {
                icon: !isMobile || !navigator.share ? icons.iconCopy : icons.iconShare,
                value: toI18N('link_detail_share_landing_page_preview'),
                urlToShare: landingPagePreviewLink,
                onClick: async () => {
                    if (!isMobile || !navigator.share) return info(toI18N('link_was_copied'));

                    const shareData = {
                        title: toI18N('generate_link_text_to_share_title'),
                        text: landingPagePreviewLink
                    };

                    // @ts-ignore
                    await navigator.share(shareData)

                }
            },
            {
                icon: icons.iconEye,
                value: toI18N('link_detail_view_landing_page_preview'),
                onClick: () => window.open(landingPagePreviewLink, "_blank")
            },
            {
                icon: !isMobile || !navigator.share ? icons.iconCopy : icons.iconShare,
                value: toI18N('link_detail_share_intro_screen_preview'),
                urlToShare: csbPreviewLink,
                onClick: async () => {
                    if (!isMobile || !navigator.share) return info(toI18N('link_was_copied'));

                    const shareData = {
                        title: toI18N('generate_link_text_to_share_title'),
                        text: csbPreviewLink
                    };

                    // @ts-ignore
                    await navigator.share(shareData)
                }
            },
            {icon: icons.iconEye, value: toI18N('link_detail_view_intro_screen_preview'), onClick: onOpenPreview}
        ]
    }, [csbPreviewLink, landingPagePreviewLink, info, onOpenPreview]);

    return (
        <Screen
            title={"link_detail_screen_header_title"}
            disabledKeyDown
            subtitle={"link_detail_screen_header_subtitle"}
            onBack={onBack}
            hideNext
            onNext={handleOnNext}
            disabledNext={!props.store.canProceed}>

            <Alert ref={alertRef}/>

            <Box center topMargin={isMobile ? 45 : 100}>
                <Title shrink title={toI18N('link_detail_title')}/>
            </Box>

            <Box grow center bottomMargin={isMobile ? 60 : 80}>
                <Box direction={Direction.ROW} center >
                    <Box><h6 style={{fontSize: '1rem'}}>https://</h6></Box>
                    <TextInput inline placeholder={toI18N("select_group_id_placeholder")}
                               id={"group-select-id-create-group-screen"}
                               maxLength={32}
                               label={toI18N("select_group_id_label")}
                               autoComplete={"off"} // Trick to make chrome not to auto-fill this field when selecting email
                               value={selectId}
                               errorMessage={isSelectIdAvailable ? '' : toI18N("select_group_id_error")}
                               hasLoadingIndicator={true}
                               isLoading={isCheckingSelectId}
                               allowSpaces={false}
                               onChange={(value: string) => {
                                   checkingForSelectId(value); // For loading indicator
                                   setSelectIdIfPossible(value);
                               }}/>

                    <Box><h6 style={{fontSize: '1rem'}}>.select.id/{linkType === LINK_TYPE_INVITATION_PAGE ? <span style={{color: '#fc654c'}}>lpox</span> : ''}</h6></Box>
                </Box>
                <div className={classNames(appStyles.separator, appStyles.sideMargin)}/>
                <Box grow direction={Direction.Column} center top>
                    <Box bottomMargin={12} sideMargin>
                        <h4>{toI18N("Use a link for...")}</h4>
                    </Box>
                    <Box bottomMargin={12}  sideMargin>
                        <label style={{display: 'flex', alignItems: 'center'}}>
                            <input
                                type="radio"
                                value={LINK_TYPE_COMMUNITY_PAGE}
                                checked={linkType === LINK_TYPE_COMMUNITY_PAGE}
                                onChange={onValueChange}
                                style={{marginRight: 12}}
                            />
                            <Title  color={ "#002b3b"} center={false}
                                   title={toI18N("link_detail_link_type_community_page_label")}/>
                        </label>
                        <label className={textStyles.hint}>
                            {toI18N('link_detail_link_type_community_page_hint')}
                        </label>
                    </Box>
                    <Box bottomMargin={12}  sideMargin>
                        <label style={{display: 'flex', alignItems: 'center'}}>
                            <input
                                type="radio"
                                value={LINK_TYPE_INVITATION_PAGE}
                                checked={linkType === LINK_TYPE_INVITATION_PAGE}
                                onChange={onValueChange}
                                style={{marginRight: 12}}
                            />
                            <Title color={"#002b3b"} center={false}
                                   title={toI18N("link_detail_link_type_invitation_page_label")}/>
                        </label>
                        <label className={textStyles.hint}>
                            {toI18N('link_detail_link_type_invitation_page_hint')}
                        </label>
                    </Box>
                </Box>

                <Box fullWidth sideMargin direction={Direction.Column}>
                    <Box spaceBetween shrink bottomMargin={12}>
                        <label htmlFor={'default-landing-page-checkbox'}
                               className={classNames([radioButtonStyles.label, radioButtonStyles.checkboxLabel])}>
                            {toI18N("link_detail_display_group_representative")}
                        </label>
                        <input
                            id={'default-landing-page-checkbox'}
                            type="checkbox"
                            checked={hasInviter}
                            onChange={(event) => {
                                setHasInviter(event.target.checked);
                            }}
                            name={"link_detail_default_landing_page"}
                        />
                    </Box>

                    <Box
                        shrink
                        style={{position: 'relative', opacity: !hasInviter ? 0.15 : 1}}
                       >
                    <AvatarWithText
                        color={textColor}
                        avatarClassName={avatarStyles.inviterAvatar}
                        bottomMargin={10}

                        title={user.name}
                        src={user.avatar}
                        subtitle={toI18N('landing_user_invited_you')}/>
                    </Box>
                </Box>
                <Box  direction={Direction.Column} center>

                    <Box className={appStyles.columnVerticalCenter}
                        topMargin={isMobile ? 12 : 26}
                        direction={Direction.Column}
                        sideMargin
                        shrink
                        center>

                        <Box style={{width: "85%"}}
                            className={classNames(styles.overflowMenuAnchor, {[styles.visibleOnModal]: showPreviewOptions})}>
                            <Button className={classNames(styles.fullWidth)} primary
                                onClick={() => {
                                    if (!imagesAreValid) {
                                        info(toI18N('info_image_still_upload'));
                                        reUploadAll();
                                        return
                                    }
                                    window.open(landingPagePreviewLink, "_blank");
                                }}>
                                <Box center>
                                    <Box grow center>
                                        {toI18N('preview_screen_title')}
                                    </Box>

                                </Box>

                            </Button>
                            <Box
                                className={classNames(styles.overflowMenu, styles.overflowMenuTop, {
                                    [styles.overflowMenuVisible]: showPreviewOptions
                                })}>
                                {OPTIONS.map(({value, onClick, icon, urlToShare}, i) => {
                                        if (!urlToShare) {
                                            return (
                                                <ModalDialogOption icon={icon}
                                                                   first={i === 0}
                                                                   key={`share-value${i}`}
                                                                   last={i === OPTIONS.length - 1}
                                                                   onClick={onClick}
                                                                   iconMargin={8}
                                                                   value={value}/>
                                            )
                                        }
                                        return (
                                            // @ts-ignore
                                            <CopyToClipboard key={`share-value${i}`} onCopy={onClick} text={urlToShare}>
                                                <ModalDialogOption icon={icon}
                                                                   last={i === OPTIONS.length - 1}
                                                                   first={i === 0}
                                                                   iconMargin={8}
                                                                   value={value}/>
                                            </CopyToClipboard>
                                        )
                                    }
                                )}
                            </Box>
                        </Box>

                        <Box topMargin={4}>
                            <label className={textStyles.hint}>
                                {toI18N('link_detail_holistic_view_hint')}
                            </label>
                        </Box>

                    </Box>

                </Box>


            </Box>

            <Box className={classNames(styles.nextButtonRightContainer)} rightMargin={20}
                 bottomMargin={isMobile ? 20 : 26}>
                <Button disabled={!props.store.canProceed || props.store.isNewGroup ? !canProceedForCreatingGroup : !canProceedForSelectingGroup} primary onClick={onProceed}>
                    <Icon white name={icons.iconArrowRight}/>
                </Button>
            </Box>

            <div
                onClick={setShowPreviewOptions.bind(null, false)}
                className={classNames(modalStyles.modal, {
                    [modalStyles.modalShow]: showPreviewOptions,
                })}/>

                <Modal id={"loading-modal-detail-screen"} center show={isCheckingWords} >
                    <LoadingSpinner xl/>
                </Modal>

                <ModalDetectHarmfulWords
                    id={`link-details-modal-harmful`}
                    disabled={!emailCanProceed || !email}
                    words={harmfulWords}
                    onDismiss={setShowModalDetectHarmfulWords.bind(null, false)}
                    onConfirm={onContinueWithHarmfulWords}
                    email={email}
                    setEmail={setEmail}
                    show={showModalDetectHarmfulWords}
                />

            <ModalConfirmation
                id={`link-details-modal-confirmation`}
                message={toI18N("link_details_are_you_done_customizing_link")}
                description={toI18N('link_details_disclaimer_link')}
                onDismiss={setShowModal.bind(null, false)}
                onConfirm={onFinishCustomizing}
                show={showModal}
            />

        </Screen>
    );
}


export default observer(LinkDetailsScreen);
